import {
  addDays,
  formatDistance,
  formatDistanceToNow,
  formatDistanceToNowStrict,
  isAfter,
  isToday,
  parseISO,
  // sub as subFns,
} from 'date-fns'
import addHoursFns from 'date-fns/addHours'
import addMinutesFns from 'date-fns/addMinutes'
import addSecondsFns from 'date-fns/addSeconds'
import addYearsFns from 'date-fns/addYears'
import differenceInCalendarDaysFns from 'date-fns/differenceInCalendarDays'
import differenceInHoursFns from 'date-fns/differenceInHours'
import endOfDayFns from 'date-fns/endOfDay'
import endOfYearFns from 'date-fns/endOfYear'
import format from 'date-fns/format'
import hoursToMinutes from 'date-fns/hoursToMinutes'
import isBeforeFns from 'date-fns/isBefore'
import isSameDayFns from 'date-fns/isSameDay'
import isValidFns from 'date-fns/isValid'
import minutesToSeconds from 'date-fns/minutesToSeconds'
import startOfDayFns from 'date-fns/startOfDay'
import startOfYearFns from 'date-fns/startOfYear'
import subFns from 'date-fns/sub'
import { utcToZonedTime } from 'date-fns-tz'
import moment from 'moment'

import { WORKDAYS_VALUES } from 'containers/Settings/General/constants'

import 'moment-timezone'

// Time constants
const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 3600

// Previous period text constants
const PREVIOUS_DAY_TEXT = 'previous day'
const PREVIOUS_YEAR_TEXT = 'previous year'

const DATE_FORMAT_MONTH_FIRST_YEAR = 'MMM d, yyyy'
const DATE_FORMAT_DAY_FIRST_YEAR = 'd MMM, yyyy'

/** Treat null/invalid/unix-epoch (`new Date(null)` / `0`) as missing timestamps. */
const isMeaningfulTimestamp = (value) => {
  if (value == null || value === '') {
    return false
  }
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) {
    return false
  }
  return date.getTime() > 0
}

export const formateDesignSystemDate = (
  date,
  options = {
    withYear: false,
    withMinutes: false,
    withSeconds: false,
    showOnlyHours: false,
    use24Hour: false,
    roundUp59Minutes: false,
    monthFirst: false,
  }
) => {
  const {
    withYear,
    withMinutes,
    withSeconds,
    showOnlyHours,
    use24Hour,
    roundUp59Minutes,
    monthFirst,
    fallback,
  } = options

  if (fallback !== undefined) {
    if (!isMeaningfulTimestamp(date)) {
      return fallback
    }
    date = date instanceof Date ? date : new Date(date)
  }

  if (showOnlyHours) {
    const formattedTime = format(date, use24Hour ? 'HH:mm' : 'h:mm')
    // Round up any time ending with :59 to the next hour (only if enabled)
    if (roundUp59Minutes && formattedTime.endsWith(':59')) {
      const roundedDate = new Date(date)
      roundedDate.setMinutes(0)
      roundedDate.setHours(roundedDate.getHours() + 1)
      return format(roundedDate, use24Hour ? 'HH:mm' : 'h:mm')
    }
    return formattedTime
  }

  // Full date with day, month, year, and time (e.g., 24 Nov 2024, 08:54:07 AM)
  if (withYear && withMinutes && withSeconds) {
    const dateFormat = monthFirst ? 'MMM dd yyyy' : 'dd MMM yyyy'
    return format(date, use24Hour ? `${dateFormat}, HH:mm:ss` : `${dateFormat}, hh:mm:ss a`)
  }

  let datePart = monthFirst ? 'MMM d' : 'd MMM'
  if (withYear) {
    datePart = monthFirst ? DATE_FORMAT_MONTH_FIRST_YEAR : DATE_FORMAT_DAY_FIRST_YEAR
  }

  let timePart = ''
  if (withMinutes && withSeconds) {
    timePart = use24Hour ? ', HH:mm:ss' : ', h:mm:ss a'
  } else if (withMinutes) {
    timePart = use24Hour ? ', HH:mm' : ', h:mm a'
  } else if (withSeconds) {
    timePart = use24Hour ? ', H:ss' : ', h:ss a'
  } else if (withYear) {
    // Add comma after year when no time components
    datePart = monthFirst ? DATE_FORMAT_MONTH_FIRST_YEAR : DATE_FORMAT_DAY_FIRST_YEAR
    timePart = ''
  }

  const formatString = datePart + timePart
  let formattedDate = format(date, formatString)

  // Round up any time ending with :59 to the next hour when time is included (only if enabled)
  if (roundUp59Minutes && (withMinutes || withSeconds)) {
    const timeRegex = use24Hour ? /(\d{1,2}):59/g : /(\d{1,2}):59\s*(AM|PM)/gi
    formattedDate = formattedDate.replace(timeRegex, (match, hour, period = '') => {
      const nextHour = Number.parseInt(hour) + 1
      if (use24Hour) {
        // Handle day rollover for 24-hour format
        const displayHour = nextHour === 24 ? 0 : nextHour
        return `${displayHour.toString().padStart(2, '0')}:00`
      } else {
        // Handle 12-hour format
        let displayHour = nextHour
        let newPeriod = period
        if (nextHour === 13) {
          displayHour = 1
        } else if (nextHour === 24) {
          displayHour = 12
        } else if (nextHour === 12) {
          // Toggle AM/PM when reaching 12
          newPeriod = period === 'AM' ? 'PM' : 'AM'
        }
        return `${displayHour}:00 ${newPeriod}`
      }
    })
  }

  return formattedDate
}

export const getTZOffset = () => {
  const date = new Date()
  const offsetMinutes = date.getTimezoneOffset() // Returns offset in minutes (negative for ahead of UTC)
  const hours = Math.floor(Math.abs(offsetMinutes) / 60)
    .toString()
    .padStart(2, '0')
  const minutes = (Math.abs(offsetMinutes) % 60).toString().padStart(2, '0')
  const sign = offsetMinutes <= 0 ? '+' : '-' // Positive sign if ahead of UTC
  const offset = `${sign}${hours}:${minutes}`
  return offset
}

/**
 * Gets the UTC offset in format UTC-04 or UTC+04
 * @param {Date} date - The date to get the offset for (defaults to current date)
 * @returns {string} UTC offset string, e.g., "UTC-04" or "UTC+05"
 */
export const getUTCOffset = (date = new Date()) => {
  // getTimezoneOffset() returns offset from UTC to local time (positive when local is behind UTC)
  const offsetMinutes = date.getTimezoneOffset()
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60)
  // If offsetMinutes is positive, local is behind UTC (e.g., EDT is UTC-04)
  // If offsetMinutes is negative, local is ahead of UTC (e.g., UTC+05)
  const sign = offsetMinutes > 0 ? '-' : '+'
  return `UTC${sign}${offsetHours.toString().padStart(2, '0')}`
}

export const isDateRangeWithinHours = (startDate, endDate, maxHours = 30) =>
  (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60) <= maxHours

export const isDateRangeOver12Months = (firstDate, lastDate) => {
  const MONTHS_IN_YEAR = 12
  const start = new Date(firstDate)
  const end = new Date(lastDate)

  const yearDiff = end.getFullYear() - start.getFullYear()
  const monthDiff = end.getMonth() - start.getMonth()

  // eslint-disable-next-line no-mixed-operators
  const totalMonths = yearDiff * MONTHS_IN_YEAR + monthDiff

  return totalMonths > MONTHS_IN_YEAR
}

export const isDateRangeWithinDays = (startDate, endDate, maxDays = 7, inclusive = true) => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffTime = Math.abs(end - start) + (inclusive ? 1 : 0)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays <= maxDays
}

export const dateFormats = {
  short: 'hh:mm a',
  regular: 'd-MMM-yyyy',
  shortYear: 'd-MMM-yy',
  extended: 'd-MMM-yyyy, h:mm:ss a',
  numbers: 'yyyy-MM-dd',
  primary: DATE_FORMAT_MONTH_FIRST_YEAR,
  secondary: 'MMM d, yyyy h:mm',
  fullDateTime: 'MMM d, yyyy h:mm:ss a',
}

export const DATE_VALUES = {
  DAY: 0,
  YESTERDAY: 8,
  WEEK: 1,
  MONTH: 7,
  DAYS_BACK_90: 2,
  DAYS_BACK_180: 3,
  LAST_YEAR: 4,
  CURRENT_YEAR: 5,
  CUSTOM: 9,
  LAST_HOUR: 10,
  LAST_12_HOURS: 11,
  LAST_24_HOURS: 12,
  ALL_TIME: null,
  DAYS_BACK_60: 13,
  DAYS_BACK_120: 14,
  LAST_2_YEARS: 15,
  OLD_ALL_TIMES: 6,
}

export const DATE_FILTER_LABELS = {
  TODAY: 'Today',
  YESTERDAY: 'Yesterday',
  LAST_24_HOURS: 'Last Day',
  LAST_HOUR: 'Last Hour',
  LAST_12_HOURS: 'Last 12 Hours',
  LAST_DAY: 'Last Day',
  DAYS_BACK_SEVEN: 'Last 7 days',
  WEEK: 'Last 7 days',
  DAYS_BACK_THIRTY: 'Last 30 days',
  MONTH: 'Last 30 days',
  DAYS_BACK_SIXTY: 'Last 60 days',
  DAYS_BACK_90: 'Last 90 days',
  DAYS_BACK_120: 'Last 120 days',
  DAYS_BACK_180: 'Last 180 days',
  CURRENT_YEAR: 'Current year',
  LAST_YEAR: 'Last year',
  LAST_2_YEARS: 'Last 2 years',
  ALL_TIME: 'All times',
  CUSTOM: 'Custom',
}

export const DATE_FILTERS = [
  { value: DATE_VALUES.DAY, label: DATE_FILTER_LABELS.TODAY },
  { value: DATE_VALUES.LAST_24_HOURS, label: DATE_FILTER_LABELS.LAST_24_HOURS },
  { value: DATE_VALUES.YESTERDAY, label: DATE_FILTER_LABELS.YESTERDAY },
  { value: DATE_VALUES.WEEK, label: DATE_FILTER_LABELS.WEEK },
  { value: DATE_VALUES.MONTH, label: DATE_FILTER_LABELS.MONTH },
  { value: DATE_VALUES.DAYS_BACK_90, label: DATE_FILTER_LABELS.DAYS_BACK_90 },
  { value: DATE_VALUES.DAYS_BACK_180, label: DATE_FILTER_LABELS.DAYS_BACK_180 },
  { value: DATE_VALUES.CURRENT_YEAR, label: DATE_FILTER_LABELS.CURRENT_YEAR },
  { value: DATE_VALUES.LAST_YEAR, label: DATE_FILTER_LABELS.LAST_YEAR },
  { value: DATE_VALUES.LAST_2_YEARS, label: DATE_FILTER_LABELS.LAST_2_YEARS },
  { value: DATE_VALUES.ALL_TIME, label: DATE_FILTER_LABELS.ALL_TIME },
  { value: DATE_VALUES.CUSTOM, label: DATE_FILTER_LABELS.CUSTOM },
]

export const DEFAULT_ARRIVAL_DATE_FILTER = DATE_VALUES.MONTH
export const DEFAULT_LOG_DATE_FILTER = DATE_VALUES.ALL_TIME

export const THREAT_ARRIVAL_DATE_FILTERS = [
  { value: DATE_VALUES.LAST_HOUR, label: DATE_FILTER_LABELS.LAST_HOUR, preLabel: '1h' },
  { value: DATE_VALUES.LAST_12_HOURS, label: DATE_FILTER_LABELS.LAST_12_HOURS, preLabel: '12h' },
  { value: DATE_VALUES.LAST_24_HOURS, label: DATE_FILTER_LABELS.LAST_DAY, preLabel: '24h' },
  { value: DATE_VALUES.WEEK, label: DATE_FILTER_LABELS.WEEK, preLabel: '7d' },
  { value: DATE_VALUES.MONTH, label: DATE_FILTER_LABELS.MONTH, preLabel: '30d' },
  { value: DATE_VALUES.DAYS_BACK_60, label: DATE_FILTER_LABELS.DAYS_BACK_SIXTY, preLabel: '60d' },
  { value: DATE_VALUES.DAYS_BACK_90, label: DATE_FILTER_LABELS.DAYS_BACK_90, preLabel: '90d' },
  { value: DATE_VALUES.DAYS_BACK_120, label: DATE_FILTER_LABELS.DAYS_BACK_120, preLabel: '120d' },
  { value: DATE_VALUES.DAYS_BACK_180, label: DATE_FILTER_LABELS.DAYS_BACK_180, preLabel: '180d' },
  { value: DATE_VALUES.LAST_YEAR, label: DATE_FILTER_LABELS.LAST_YEAR, preLabel: '1y' },
  { value: DATE_VALUES.CUSTOM, label: DATE_FILTER_LABELS.CUSTOM, icon: 'calendar' },
]

export const ACCOUNT_INVESTIGATION_ARRIVAL_DATE_FILTERS = [
  {
    value: DATE_VALUES.LAST_HOUR,
    label: DATE_FILTER_LABELS.LAST_HOUR,
    preLabel: '1h',
    disabled: false,
  },
  {
    value: DATE_VALUES.LAST_12_HOURS,
    label: DATE_FILTER_LABELS.LAST_12_HOURS,
    preLabel: '12h',
    disabled: false,
  },
  {
    value: DATE_VALUES.LAST_24_HOURS,
    label: DATE_FILTER_LABELS.LAST_DAY,
    preLabel: '24h',
    disabled: false,
  },
  { value: DATE_VALUES.WEEK, label: DATE_FILTER_LABELS.WEEK, preLabel: '7d', disabled: false },
  { value: DATE_VALUES.MONTH, label: DATE_FILTER_LABELS.MONTH, preLabel: '30d', disabled: true },
  {
    value: DATE_VALUES.DAYS_BACK_60,
    label: DATE_FILTER_LABELS.DAYS_BACK_SIXTY,
    preLabel: '60d',
    disabled: true,
  },
  {
    value: DATE_VALUES.DAYS_BACK_90,
    label: DATE_FILTER_LABELS.DAYS_BACK_90,
    preLabel: '90d',
    disabled: true,
  },
  {
    value: DATE_VALUES.DAYS_BACK_120,
    label: DATE_FILTER_LABELS.DAYS_BACK_120,
    preLabel: '120d',
    disabled: true,
  },
  {
    value: DATE_VALUES.DAYS_BACK_180,
    label: DATE_FILTER_LABELS.DAYS_BACK_180,
    preLabel: '180d',
    disabled: true,
  },
  { value: DATE_VALUES.CUSTOM, label: DATE_FILTER_LABELS.CUSTOM, icon: 'calendar', disabled: true },
]

export const THREAT_LOG_DATE_FILTERS = [
  { value: DATE_VALUES.LAST_HOUR, label: DATE_FILTER_LABELS.LAST_HOUR, preLabel: '1h' },
  { value: DATE_VALUES.LAST_12_HOURS, label: DATE_FILTER_LABELS.LAST_12_HOURS, preLabel: '12h' },
  { value: DATE_VALUES.LAST_24_HOURS, label: DATE_FILTER_LABELS.LAST_DAY, preLabel: '24h' },
  { value: DATE_VALUES.WEEK, label: DATE_FILTER_LABELS.WEEK, preLabel: '7d' },
  { value: DATE_VALUES.MONTH, label: DATE_FILTER_LABELS.MONTH, preLabel: '30d' },
  { value: DATE_VALUES.DAYS_BACK_60, label: DATE_FILTER_LABELS.DAYS_BACK_SIXTY, preLabel: '60d' },
  { value: DATE_VALUES.DAYS_BACK_90, label: DATE_FILTER_LABELS.DAYS_BACK_90, preLabel: '90d' },
  { value: DATE_VALUES.DAYS_BACK_120, label: DATE_FILTER_LABELS.DAYS_BACK_120, preLabel: '120d' },
  { value: DATE_VALUES.DAYS_BACK_180, label: DATE_FILTER_LABELS.DAYS_BACK_180, preLabel: '180d' },
  { value: DATE_VALUES.LAST_YEAR, label: DATE_FILTER_LABELS.LAST_YEAR, preLabel: '1y' },
  { value: DATE_VALUES.ALL_TIME, label: DATE_FILTER_LABELS.ALL_TIME, preLabel: 'All' },
  { value: DATE_VALUES.CUSTOM, label: DATE_FILTER_LABELS.CUSTOM, icon: 'calendar' },
]

export const HOME_DASHBOARD_DATE_FILTERS = [
  { value: DATE_VALUES.LAST_24_HOURS, label: DATE_FILTER_LABELS.LAST_DAY },
  { value: DATE_VALUES.WEEK, label: DATE_FILTER_LABELS.WEEK },
  { value: DATE_VALUES.MONTH, label: DATE_FILTER_LABELS.MONTH },
  { value: DATE_VALUES.DAYS_BACK_90, label: DATE_FILTER_LABELS.DAYS_BACK_90 },
  { value: DATE_VALUES.DAYS_BACK_180, label: DATE_FILTER_LABELS.DAYS_BACK_180 },
  { value: DATE_VALUES.LAST_YEAR, label: DATE_FILTER_LABELS.LAST_YEAR },
  { value: DATE_VALUES.CUSTOM, label: DATE_FILTER_LABELS.CUSTOM },
]

export const INCIDENT_SUMMARY_DASHBOARD_DATE_FILTERS = [
  { value: DATE_VALUES.LAST_24_HOURS, label: DATE_FILTER_LABELS.LAST_DAY },
  { value: DATE_VALUES.WEEK, label: DATE_FILTER_LABELS.WEEK },
  { value: DATE_VALUES.MONTH, label: DATE_FILTER_LABELS.MONTH },
  { value: DATE_VALUES.DAYS_BACK_90, label: DATE_FILTER_LABELS.DAYS_BACK_90 },
  { value: DATE_VALUES.DAYS_BACK_180, label: DATE_FILTER_LABELS.DAYS_BACK_180 },
  { value: DATE_VALUES.LAST_YEAR, label: DATE_FILTER_LABELS.LAST_YEAR },
  { value: DATE_VALUES.CUSTOM, label: DATE_FILTER_LABELS.CUSTOM },
]

export const deprecatedDateFilterOptions = new Set([
  DATE_VALUES.CURRENT_YEAR,
  DATE_VALUES.ALL_TIME,
  DATE_VALUES.LAST_2_YEARS,
  DATE_VALUES.DAY,
  DATE_VALUES.YESTERDAY,
])

export const CAMPAIGN_DATE_FILTERS_OPTIONS = [
  ...DATE_FILTERS.filter(
    (x) => x.value === DATE_VALUES.ALL_TIME || !deprecatedDateFilterOptions.has(x.value)
  ),
]

const fd = (date, customFormat, empty = 'N/A', isWrapNewDate = true) => {
  try {
    const newDate = isWrapNewDate ? new Date(date) : date

    return date ? format(newDate, customFormat) : empty
  } catch (err) {
    return ''
  }
}

const CALENDAR_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})/

/**
 * Parse a wizard calendar date (`YYYY-MM-DD`) as a local date.
 * `new Date('YYYY-MM-DD')` is UTC midnight, so west-of-UTC timezones show the previous day.
 */
export const parseCalendarDate = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  const match = String(value ?? '').match(CALENDAR_DATE_ONLY)
  if (!match) {
    return null
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

export const customFormat = (date, dateFormat, isWrapNewDate = true) =>
  fd(date, dateFormat, undefined, isWrapNewDate)

/**
 * Formats a date with UTC offset
 * @param {Date|string} date - The date to format
 * @param {string} dateFormat - The date format string (e.g., 'dd MMM, yyyy, HH:mm:ss')
 * @returns {string} The formatted date string with UTC offset
 */
export const formatDateWithUTCOffset = (date, dateFormat = 'dd MMM, yyyy, HH:mm:ss') => {
  const receivedDate = new Date(date)
  const utcDate = getUTCOffset(receivedDate)
  return `${customFormat(receivedDate, dateFormat, false)} (${utcDate})`
}

export const toUTC = (date) => {
  const receivedDate = new Date(date)
  const utcTime = Date.UTC(
    receivedDate.getFullYear(),
    receivedDate.getMonth(),
    receivedDate.getDate(),
    receivedDate.getHours(),
    receivedDate.getMinutes(),
    receivedDate.getSeconds(),
    receivedDate.getMilliseconds()
  )
  const utcDate = new Date(utcTime).toISOString()
  const parsedTime = parseISO(utcDate)
  const formattedTime = utcToZonedTime(parsedTime, 'UTC')
  return formattedTime.toISOString()
}

export const fromUTC = (date) => {
  const receivedDate = new Date(date)
  const utcTime = Date.UTC(
    receivedDate.getFullYear(),
    receivedDate.getMonth(),
    receivedDate.getDate(),
    receivedDate.getHours(),
    receivedDate.getMinutes(),
    receivedDate.getSeconds(),
    receivedDate.getMilliseconds()
  )
  return new Date(utcTime)
}

export const formatWithoutTimezone = (
  date,
  receivedFormat = dateFormats.regular,
  empty = 'N/A'
) => {
  const receivedDate = new Date(date)
  const utcDate = new Date(toUTC(receivedDate))
  return date ? format(utcDate, receivedFormat) : empty
}

export const getMinutes = () => {
  let i = 0
  const limit = 60
  const minutes = []
  for (i; i < limit; i += 1) {
    minutes.push(i < 10 ? `0${i}` : `${i}`)
  }
  return minutes
}

export const dateToTimeZone = (date) => {
  const receivedDate = new Date(date)
  const hours = receivedDate.getHours()
  const offset = receivedDate.getTimezoneOffset() / 60
  receivedDate.setHours(hours + offset)
  return receivedDate
}

export const utcToCurrentTimeZone = (date) => {
  const utcDate = new Date(date).toISOString()
  const parsedTime = parseISO(utcDate)
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const formattedTime = utcToZonedTime(parsedTime, tz)
  return formattedTime
}

export const formatDate = (date, empty) => fd(date, dateFormats.extended, empty)

export const formatToTimeZone = (date) => {
  const receivedDate = dateToTimeZone(date)
  return formatDate(receivedDate)
}
/**
 * Formats a date to the short format: DD-MMM-YYYY (e.g. 21-Jul-2014) without time
 * @param {Date|string} date - The date to format
 * @param {string} [empty] - The string to return if date is falsy
 * @returns {string} The formatted date string or empty string if provided
 */
export const formatDateShort = (date, empty) => fd(date, dateFormats.regular, empty)

export const formatDateShortYear = (date, empty) => fd(date, dateFormats.shortYear, empty)

export const formatDateLog = (date) => {
  const recivedDate = new Date(date)
  const now = new Date()
  if (isAfter(recivedDate, subFns(now, { hours: 12 }))) {
    return formatDistance(recivedDate, now)
  } else if (isSameDay(recivedDate, now)) {
    return `Today ${format(recivedDate, dateFormats.short)}`
  } else {
    return fd(recivedDate, dateFormats.extended)
  }
}

export const smartFormat = (date) => {
  const recivedDate = new Date(date)
  const thismoment = new Date()
  return isSameDay(thismoment, recivedDate)
    ? format(recivedDate, dateFormats.short)
    : format(recivedDate, dateFormats.regular)
}

export const formatCommentDate = (date) => {
  const recivedDate = new Date(date)
  const now = new Date()
  if (isAfter(recivedDate, subFns(now, { days: 1 }))) {
    return formatDistance(recivedDate, now)
  }
  return fd(recivedDate, dateFormats.regular)
}

/**
 * Formats a date string to the "DD-MMM-YYYY" format (e.g., "21-Jul-2014").
 * Handles dates with or without time components.
 *
 * @param {string} date - The date string to format (e.g., "MM/DD/YYYY" or "MM/DD/YYYY HH:mm:ss").
 * @returns {string} The formatted date string in the "DD-MMM-YYYY" format.
 */
export const formatToUser = (date) => {
  // Remove time component if present
  const dateWithoutTime = date.split(' ')[0]
  // Split the date string into day, month, and year parts
  const parts = dateWithoutTime.split('/')
  const day = parts[0]
  const month = parts[1]
  const year = parts[2]
  // Create a Date object using the American format (MM/DD/YYYY)
  const formattedDate = format(new Date(`${month}/${day}/${year}`), dateFormats.regular)

  return formattedDate
}

export const getFromCustomDate = (fromDate) => ({
  value: DATE_VALUES.CUSTOM,
  label: `${format(new Date(fromDate), dateFormats.regular)} - Today`,
})

export const mapDateFilterToFormattedDate = {
  Today: formatDateShort(new Date()),
  Yesterday: formatDateShort(subFns(new Date(), { days: 1 })),
  'Last Day': formatDateShort(subFns(new Date(), { days: 1 })),
  'Last 7 days': formatDateShort(subFns(new Date(), { days: 7 })),
  'Last 30 days': formatDateShort(subFns(new Date(), { days: 30 })),
  'Last 90 days': formatDateShort(subFns(new Date(), { days: 90 })),
  'Last 180 days': formatDateShort(subFns(new Date(), { days: 180 })),
  'Last year': formatDateShort(subFns(new Date(), { years: 1 })),
  'Last 2 years': formatDateShort(subFns(new Date(), { years: 2 })),
  'Current year': formatDateShort(new Date(new Date().getFullYear(), 0, 1)),
}

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export const getStartDate = (currentDateFilter, options = DATE_FILTERS) => {
  if (Array.isArray(currentDateFilter)) return currentDateFilter[0]
  const key = options.find((val) => val.value === currentDateFilter)?.label
  if (key) return mapDateFilterToFormattedDate[key]
  return null
}

export const getParsedDateUntilToday = (currentDateFilter, options = DATE_FILTERS) => {
  if (currentDateFilter === DATE_VALUES.ALL_TIME) return '(All times)'

  const startDate = getStartDate(currentDateFilter, options)

  if (Array.isArray(currentDateFilter)) {
    return `(${getCustomRange(currentDateFilter, false, options).label})`
  } else if (startDate) {
    return `(${startDate} - ${currentDateFilter !== 0 ? formatDateShort(new Date()) : startDate})`
  }
}

export const isBefore = (date, compareDate) => isBeforeFns(date, compareDate)

export const momentNowConvertedToTZ = (tz) =>
  moment(moment(moment.tz(moment(), tz).format('LT'), ['h:mm A']).format('HH:mm'), 'hh:mm')
export const getTimezoneDate = (timezone, date = new Date()) =>
  utcToZonedTime(date || new Date(), timezone)

export const WIZARD_DATE_FORMAT = 'YYYY-MM-DD' // TODO: replace use cases with this format to date-fns with dateFormats.numbers in wizard campaign
export const LAUNCH_TIME_FORMAT = 'HH:mm'
export const DISPLAY_12_TIME_FORMAT = 'h:mm a'
export const DISPLAY_24_TIME_FORMAT = 'HH:mm'
export const DISPLAY_12_TIME_PLACEHOLDER = '-:-- --'
export const DISPLAY_24_TIME_PLACEHOLDER = '--:--'
export const ISO_8601_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ'
export const MS_PER_DAY = 24 * 60 * 60 * 1000

export const getTimezoneId = () => Intl.DateTimeFormat().resolvedOptions().timeZone

export const getWeekendDays = (workdays) => {
  if (workdays === WORKDAYS_VALUES.ALL_WEEK) {
    return []
  } else if (workdays === WORKDAYS_VALUES.MON_FRI) {
    return [6, 0]
  } else if (workdays === WORKDAYS_VALUES.SUN_THU) {
    return [5, 6]
  } else throw new Error('Not supported case')
}

export const getNextWorkingDay = (mDate, weekendDays, includeToday = true) => {
  if (includeToday) {
    while (true) {
      if (weekendDays.includes(mDate.day())) {
        mDate = mDate.add(1, 'day')
      } else return mDate
    }
  } else {
    while (true) {
      mDate = mDate.add(1, 'day')
      if (!weekendDays.includes(mDate.day())) return mDate
    }
  }
}

export const addDaysToDate = (date, days, forceUTC) => {
  let receivedDate = new Date(date)

  if (forceUTC) {
    receivedDate = new Date(
      Date.UTC(
        receivedDate.getFullYear(),
        receivedDate.getMonth(),
        receivedDate.getDate(),
        receivedDate.getHours()
      )
    )
  }

  const newDate = addDays(receivedDate, days)

  return forceUTC ? new Date(newDate.toUTCString().split('GMT')[0]) : newDate
}

export const addWorkingDays = (mFromDate, days, weekendDays) => {
  while (days > 0) {
    mFromDate = mFromDate.add(1, 'day')
    if (!weekendDays.includes(mFromDate.day())) {
      days -= 1
    }
  }
  return mFromDate
}

export const getCountOfWorkingDays = (mFromDate, mToDate, weekendDays) => {
  if (
    !moment(mFromDate).isValid() ||
    !moment(mToDate).isValid() ||
    isBefore(new Date(mToDate), new Date(mFromDate))
  )
    return null
  let count = 0

  while (!moment(mFromDate.format(WIZARD_DATE_FORMAT)).isSame(mToDate.format(WIZARD_DATE_FORMAT))) {
    mFromDate = mFromDate.add(1, 'day')

    if (!weekendDays.includes(mFromDate.day())) {
      count += 1
    }
  }
  return count
}

export const castFloatToHoursAndMinutes = (number) => {
  const parsedTimeObj = {
    hours: 0,
    minutes: 0,
  }
  // Separate the int from the decimal part
  if (number) {
    const hour = Math.floor(number)
    const decpart = Number((number - hour).toFixed(2)) * 60
    parsedTimeObj.hours = hour
    parsedTimeObj.minutes = decpart
  }
  return parsedTimeObj
}

/**
 * convert floating number hours to integers hours, minutes, seconds
 *
 * @param {number} timeInput number of hours in float

 * @returns {Object} hms object
 *  @property {number} seconds Number of seconds.
 *  @property {number} minutes Number of minutes.
 *  @property {number} hours Number of hours.
 */
export const hoursToHms = (timeInput) => {
  const d = Number(timeInput)
  const h = Math.floor(d)
  const minutesLeftInHours = Number(Math.fround(d - h).toPrecision(2))
  const m = hoursToMinutes(minutesLeftInHours)
  const secondsLeftInMinutes = Number((minutesLeftInHours * 60 - m).toPrecision(2))
  const s = minutesToSeconds(secondsLeftInMinutes)

  return {
    seconds: s,
    hours: h,
    minutes: m,
  }
}

export const startOfDay = (date) => startOfDayFns(new Date(date))

export const endOfDay = (date) => endOfDayFns(new Date(date))

export const addSeconds = (date, secondsToAdd) => addSecondsFns(new Date(date), secondsToAdd)
export const addMinutes = (date, minToAdd) => addMinutesFns(new Date(date), minToAdd)
export const addHours = (date, hoursToAdd) => addHoursFns(new Date(date), hoursToAdd)
export const addYears = (date, yearsToAdd) => addYearsFns(new Date(date), yearsToAdd)

export const isSameDay = (date, comparedDate) => isSameDayFns(date, comparedDate)

export const differenceInCalendarDays = (dateLeft, dateRight) =>
  differenceInCalendarDaysFns(dateLeft, dateRight)

export const differenceInHours = (dateLeft, dateRight) => differenceInHoursFns(dateLeft, dateRight)

export const isValid = (date) => isValidFns(date)

export const fromNow = (date) =>
  formatDistanceToNow(new Date(date), {
    addSuffix: false,
    includeSeconds: false,
  })

export const fromNowStrict = (date) => formatDistanceToNowStrict(new Date(date))
export const sub = (date, options) => subFns(new Date(date), options)
export const startOfYear = (date) => startOfYearFns(new Date(date))
export const endOfYear = (date) => endOfYearFns(new Date(date))

const mapDateFilterToUnix = (dateFilter) => {
  if (Array.isArray(dateFilter)) {
    const [startDate, endDate] = dateFilter
    const startUnix = Math.floor(new Date(startDate).getTime() / 1000)
    const endUnix = Math.floor(new Date(endDate).getTime() / 1000)
    return [startUnix, endUnix]
  }

  const startUnix = new Date(getStartDate(dateFilter)).getTime() / 1000
  const endUnix = Math.floor(new Date().getTime() / 1000)
  return [startUnix, endUnix]
}

/**
 * Checks if a given date is within a specified date range.
 *
 * @param {string} dateToCheck - The date to check in the format "DD/MM/YYYY HH:MM".
 * @param {Array|string} dateFilter - The date range to check against. Can be an array of two dates [startDate, endDate] or a single date string.
 * @returns {boolean} - Returns true if the date is within the range, false otherwise.
 */
export const isDateInRange = (dateToCheck, dateFilter) => {
  if (!dateToCheck) return false
  const [day, month, year, hours, minutes] = dateToCheck.split(/[\/ :]/)
  const date = new Date(year, month - 1, day, hours, minutes)
  const unixTimeToCheck = Math.floor(date.getTime() / 1000)

  const [startUnix, endUnix] = mapDateFilterToUnix(dateFilter)

  return unixTimeToCheck >= startUnix && unixTimeToCheck <= endUnix
}

export const haveIntersectionDateRangesInDateFilter = (dateFilter, range) =>
  isDateInRange(range?.start_date, dateFilter) || isDateInRange(range?.end_date, dateFilter)

const getDaysInSeconds = (amount = 1) => 24 * 3600 * 1000 * amount

const rangeToISOString = (range) => range.toISOString()

const datify = (sec) => {
  if (sec) return new Date(sec)
  return new Date()
}

const createRange = (start, end = datify()) => [start, end].map(rangeToISOString)

export const getCustomRangeFromLabel = (label, validOptions) => {
  const now = Date.now()
  const dateRanges = {
    [DATE_FILTER_LABELS.TODAY]: () => createRange(startOfDay(now)),
    [DATE_FILTER_LABELS.YESTERDAY]: () =>
      createRange(startOfDay(sub(now, { days: 1 })), endOfDay(sub(now, { days: 1 }))),
    [DATE_FILTER_LABELS.LAST_24_HOURS]: () => createRange(sub(now, { hours: 24 })),
    [DATE_FILTER_LABELS.LAST_HOUR]: () => createRange(sub(now, { hours: 1 })),
    [DATE_FILTER_LABELS.LAST_12_HOURS]: () => createRange(sub(now, { hours: 12 })),
    [DATE_FILTER_LABELS.LAST_DAY]: () => createRange(sub(now, { days: 1 })),
    [DATE_FILTER_LABELS.DAYS_BACK_SEVEN]: () => createRange(sub(now, { days: 7 })),
    [DATE_FILTER_LABELS.DAYS_BACK_THIRTY]: () => createRange(sub(now, { days: 30 })),
    [DATE_FILTER_LABELS.DAYS_BACK_SIXTY]: () => createRange(sub(now, { days: 60 })),
    [DATE_FILTER_LABELS.DAYS_BACK_90]: () => createRange(sub(now, { days: 90 })),
    [DATE_FILTER_LABELS.DAYS_BACK_120]: () => createRange(sub(now, { days: 120 })),
    [DATE_FILTER_LABELS.DAYS_BACK_180]: () => createRange(sub(now, { days: 180 })),
    [DATE_FILTER_LABELS.CURRENT_YEAR]: () => createRange(startOfYear(now)),
    [DATE_FILTER_LABELS.LAST_YEAR]: () => createRange(sub(now, { years: 1 })),
    [DATE_FILTER_LABELS.LAST_2_YEARS]: () => createRange(sub(now, { years: 2 })),
    [DATE_FILTER_LABELS.ALL_TIME]: () => null,
  }

  if (label in dateRanges) {
    if (validOptions) {
      if (validOptions.find((o) => o.label === label)) {
        return dateRanges[label]()
      } else return null
    } else return dateRanges[label]()
  }

  if (label === DATE_FILTER_LABELS.CUSTOM) {
    throw new Error('Custom date range not supported in this function')
  }

  throw new Error(`Unknown label to convert for date range: ${label}`)
}

export const getRangeFromDateFilter = (dateFilter) => {
  if (Array.isArray(dateFilter)) {
    return dateFilter
  }
  const now = Date.now()
  const dateFilterToRange = {
    [DATE_VALUES.DAY]: () => createRange(startOfDay(now), endOfDay(now)),
    [DATE_VALUES.YESTERDAY]: () =>
      createRange(startOfDay(sub(now, { days: 1 })), endOfDay(sub(now, { days: 1 }))),
    [DATE_VALUES.LAST_24_HOURS]: () => createRange(sub(now, { hours: 24 })),
    [DATE_VALUES.LAST_HOUR]: () => createRange(sub(now, { hours: 1 })),
    [DATE_VALUES.LAST_12_HOURS]: () => createRange(sub(now, { hours: 12 })),
    [DATE_VALUES.WEEK]: () => createRange(sub(now, { days: 7 })),
    [DATE_VALUES.MONTH]: () => createRange(sub(now, { days: 30 })),
    [DATE_VALUES.DAYS_BACK_60]: () => createRange(sub(now, { days: 60 })),
    [DATE_VALUES.DAYS_BACK_90]: () => createRange(sub(now, { days: 90 })),
    [DATE_VALUES.DAYS_BACK_120]: () => createRange(sub(now, { days: 120 })),
    [DATE_VALUES.DAYS_BACK_180]: () => createRange(sub(now, { days: 180 })),
    [DATE_VALUES.CURRENT_YEAR]: () => createRange(startOfYear(now)),
    [DATE_VALUES.LAST_YEAR]: () => createRange(sub(now, { years: 1 })),
    [DATE_VALUES.LAST_2_YEARS]: () => createRange(sub(now, { years: 2 })),
    [DATE_VALUES.ALL_TIME]: () => null,
  }

  return dateFilterToRange[dateFilter]?.() || dateFilter
}

/**
 * Get a custom date range option or find a matching predefined option.
 *
 * @param {Array<string>} dateFilter - An array containing two ISO date strings representing the start and end of the date range.
 * @param {boolean} [enforceCustomRange=true] - If true, always returns a custom range option.
 * @param {Array<Object>} [validOptions] - An array of valid date range options to check against.
 * @returns {Object} An object representing either a custom date range or a matching predefined option.
 */
export const getCustomRange = (dateFilter, enforceCustomRange = true, validOptions) => {
  const startDate = startOfDay(new Date(dateFilter[0]))
  const endDate = startOfDay(new Date(dateFilter[1]))

  const customOption = {
    value: DATE_VALUES.CUSTOM,
    label: `${format(startDate, dateFormats.regular)} - ${format(endDate, dateFormats.regular)}`,
  }

  if (enforceCustomRange) {
    return customOption
  }

  for (const filter of validOptions) {
    if (filter.value !== DATE_VALUES.CUSTOM && filter.value !== DATE_VALUES.ALL_TIME) {
      const [rangeStartIso, rangeEndIso] = getCustomRangeFromLabel(filter.label)
      const rangeStart = startOfDay(new Date(rangeStartIso))
      const rangeEnd = startOfDay(new Date(rangeEndIso))
      if (isSameDay(startDate, rangeStart) && isSameDay(endDate, rangeEnd)) {
        return filter
      }
    }
  }

  return customOption
}

export const showDateRange = (dateFilter, fallback) =>
  Array.isArray(dateFilter)
    ? ` (${formatDateShort(dateFilter[0])} - ${formatDateShort(dateFilter[1])})`
    : ` ${fallback}`

/**
 * Gets text describing the previous time period based on the current date filter
 * For example: "Previous 90 days" or "Previous year"
 *
 * @param {string|number|Array} dateFilter - The current date filter value
 * @returns {string} Text describing the previous period
 */
export const getPreviousPeriodText = (dateFilter) => {
  if (!dateFilter) return 'previous period'

  // Handle array-based date filters (custom date ranges)
  if (Array.isArray(dateFilter)) {
    try {
      const startDate = new Date(dateFilter[0])
      const endDate = new Date(dateFilter[1])

      // Validate dates
      if (!isValidFns(startDate) || !isValidFns(endDate)) {
        return 'previous period'
      }

      const durationMs = endDate.getTime() - startDate.getTime()

      // Handle negative or zero duration
      if (durationMs <= 0) return 'previous period'

      // Calculate durations in different units
      const durationInMinutes = durationMs / (1000 * 60)
      const durationInHours = durationInMinutes / 60
      const durationInDays = durationInHours / 24

      // Handle minute-based durations
      if (durationInMinutes < 60) {
        const minutes = Math.round(durationInMinutes)
        return `previous ${minutes} minute${minutes !== 1 ? 's' : ''}`
      }

      // Handle hour-based durations
      if (durationInHours < 24) {
        const hours = Math.round(durationInHours)
        return `previous ${hours} hour${hours !== 1 ? 's' : ''}`
      }

      // For anything more than 24 hours, express in days
      const days = Math.round(durationInDays)

      if (days === 1) return PREVIOUS_DAY_TEXT

      // Default to showing the number of days
      return `previous ${days} days`
    } catch (error) {
      console.error('Error calculating previous period text:', error)
      return 'previous period'
    }
  }

  const filterTextMap = {
    [DATE_VALUES.DAY]: PREVIOUS_DAY_TEXT,
    [DATE_VALUES.YESTERDAY]: PREVIOUS_DAY_TEXT,
    [DATE_VALUES.LAST_24_HOURS]: PREVIOUS_DAY_TEXT,
    [DATE_VALUES.LAST_HOUR]: 'previous hour',
    [DATE_VALUES.LAST_12_HOURS]: 'previous 12 hours',
    [DATE_VALUES.WEEK]: 'previous 7 days',
    [DATE_VALUES.MONTH]: 'previous 30 days',
    [DATE_VALUES.DAYS_BACK_60]: 'previous 60 days',
    [DATE_VALUES.DAYS_BACK_90]: 'previous 90 days',
    [DATE_VALUES.DAYS_BACK_120]: 'previous 120 days',
    [DATE_VALUES.DAYS_BACK_180]: 'previous 180 days',
    [DATE_VALUES.CURRENT_YEAR]: PREVIOUS_YEAR_TEXT,
    [DATE_VALUES.LAST_YEAR]: PREVIOUS_YEAR_TEXT,
    [DATE_VALUES.LAST_2_YEARS]: 'previous 2 years',
    [DATE_VALUES.ALL_TIME]: 'previous period',
  }

  return filterTextMap[dateFilter] || 'previous period'
}

/**
 * Formats a time value in seconds to a human-readable string
 * - Shows values in minutes if less than 60 minutes (3600 seconds)
 * - Shows values in hours if greater than 60 minutes
 * - For hours, rounds up if fraction is >= 0.5, otherwise rounds down
 *
 * @param {number} seconds - Time value in seconds
 * @returns {string} Formatted time string (e.g., "5 minutes", "2 hours")
 */
export const formatSeconds = (seconds) => {
  if (!seconds) return '0 minutes'

  // Less than 60 minutes (3600 seconds) - show in minutes
  if (seconds < SECONDS_PER_HOUR) {
    const minutes = Math.round(seconds / SECONDS_PER_MINUTE)
    return `${minutes} minutes`
  }

  // More than 60 minutes - show in hours with specific rounding
  const rawHours = seconds / SECONDS_PER_HOUR
  const fractionalPart = rawHours % 1

  // Round up if fraction is >= 0.5, otherwise round down
  const hours = fractionalPart >= 0.5 ? Math.ceil(rawHours) : Math.floor(rawHours)

  return `${hours} hours`
}

/**
 * Checks if a given date is within the next N days from today (inclusive).
 *
 * @param {Date|string} date - The date to check
 * @param {number} days - The number of days to check ahead
 * @returns {boolean} - Returns true if the date is today or within the next N days, false otherwise
 */
export const isWithinNextDays = (date, days) => {
  if (!date) return false

  const checkDate = new Date(date)
  const today = new Date()

  if (!isValid(checkDate)) return false

  // Check if the date is today or in the future
  if (isBefore(checkDate, startOfDay(today))) return false

  // Check if the date is within N days from today
  const daysFromNow = addDays(today, days)
  return !isAfter(checkDate, endOfDay(daysFromNow))
}

export const isLastNDaysRange = (startDateISO, endDateISO, n = 30) => {
  const start = startOfDay(parseISO(startDateISO))
  const end = startOfDay(parseISO(endDateISO))

  return isToday(end) && differenceInCalendarDays(end, start) === n
}

/**
 * Compacts a dateRange display string when start and end fall in the same month,
 * removing the redundant month name from the end part.
 *
 * "May 8 - May 14, 2026"  →  "May 8 - 14, 2026"
 * "May 29 – May 31, 2026" →  "May 29 – 31, 2026"  (preserves original separator)
 * "Apr 28 - May 4, 2026"  →  unchanged (cross-month)
 * "Apr 1–26, 2026"        →  unchanged (already compact)
 */
export const compactDateRange = (dateRange) => {
  if (!dateRange) return dateRange

  const separatorMatch = dateRange.match(/[ ]?([\u2013\u2014-])[ ]?/)
  if (!separatorMatch) return dateRange

  const parts = dateRange.split(/[ ]?[\u2013\u2014-][ ]?/)
  if (parts.length !== 2) return dateRange

  const [rawStart, rawEnd] = parts.map((p) => p.trim())
  const endHasMonth = /[A-Za-z]/.test(rawEnd)
  if (!endHasMonth) return dateRange

  const startMonth = rawStart.split(/\s+/)[0]
  const endMonth = rawEnd.split(/\s+/)[0]

  if (startMonth !== endMonth) return dateRange

  const separator = separatorMatch[0]
  const endWithoutMonth = rawEnd.replace(/^[A-Za-z]+\s+/, '')
  return `${rawStart}${separator}${endWithoutMonth}`
}
